/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { selectCurrentUserAdmin, selectCurrentUserApiToken } from "../slices/currentUser/currentUserSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import axiosInstance from '../functions/axiosInstance';
import server_config from '../ServerConfiguration';

const fevir_node_server_address = server_config["fevir_node_server_address"];

//const colors = ["#8984D8", "#82CA9B", "#FFC658", "#FF7300", "#0087FE", "#E91E63", "#00C49F", "#FF4444", "#673AB7", "#607D8B"];
const colors = ["#1F77B4", "#FF7F0E", "#2CA02C", "#D62728", "#9467BD", "#8C564B", "#E377C2", "#A994D8", "#BCBD22", "#17BECF"];

const formatDate = (dateString) => {
    if (!dateString) { return '' };
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });  // , year: 'numeric'
};

/*
const formatYAxis = (num) => {
    return Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(num);
};
*/

const formatCompactNumber = (num) => {
    if (!num) { return '0'; }
    return Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(num);
};

const fillMissingDatesAsZero = (dailyStats, totals) => {
    if (!totals || !dailyStats || dailyStats.length === 0) return [];
    const sortedData = [...dailyStats].sort((a, b) => new Date(a.date) - new Date(b.date));
    const startDate = new Date(sortedData[0].date);
    const endDate = new Date(sortedData[sortedData.length - 1].date);

    const filledData = [];
    const currentDate = new Date(startDate);
    const functionKeys = Object.keys(totals);
    while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const existingEntry = sortedData.find(item => item.date === dateStr);
        if (existingEntry) {
            filledData.push(existingEntry);
        } else {
            const zeroEntry = { date: dateStr };
            functionKeys.forEach(key => {
                zeroEntry[key] = { count: 0, users: 0 };
            });
            filledData.push(zeroEntry);
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return filledData;
};

const LegendHistoryTooltip = ({ functionName, data, position }) => {
    if (!functionName || !position) { return null }

    const history = data.filter(day => day[functionName]?.count > 0).sort((a, b) => new Date(a.date) - new Date(b.date));

    return <div style={{
        position: 'fixed',
        left: position.x + 20,
        top: position.y, //put  "- 100" or minus some other number if the tooltip goes offscreen
        backgroundColor: '#FFFFFF',
        border: '1px solid #CCCCCC',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        zIndex: 1000,
        minWidth: '200px',
        maxHeight: '300px',
        overflowY: 'auto'
    }}>
        <h4 style={{ margin: '0px 0px 10px 0px', borderBottom: '1px solid #EEEEEE', paddingBottom: '5px' }}>
            {functionName}
        </h4>
        {history.length && <div style={{ textAlign: 'right' }}>
            {history.map((day, index) => {
                return <div key={index}>{formatDate(day.date)}: <span style={{ color: '#666666' }}>{day[functionName].count} (<i>{day[functionName].users} users</i>)</span></div>
            })}
        </div>}
    </div>
};

const AnalyticsChart = ({ data }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [legendPopup, setLegendPopup] = useState({ show: false, name: null, x: 0, y: 0 });

    useEffect(() => {
        setTimeout(() => setIsLoaded(true), 2000); //Turn off the line graph animation after the initial load
    }, []);

    const chartData = useMemo(() => {
        return fillMissingDatesAsZero(data.dailystats, data.totals);
    }, [data.dailystats, data.totals]);

    const [highlight, setHighlight] = useState(null);

    if (!data || !data.dailystats || data.dailystats.length === 0) {
        return <div style={{ padding: 20 }}>No data available</div>;
    }

    const functionKeys = Object.keys(data.totals);

    const handleLegendEnter = (e, funcName) => {
        setHighlight(funcName);
        setLegendPopup({
            show: true,
            name: funcName,
            x: e.clientX,
            y: e.clientY
        });
    };

    const handleLegendMove = (e) => {
        // Update position as mouse moves so tooltip follows cursor
        if (legendPopup.show) {
            setLegendPopup(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
        }
    };

    const handleLegendLeave = () => {
        setHighlight(null);
        setLegendPopup({ show: false, name: null, x: 0, y: 0 });
    };

    const renderExternalLegend = () => {
        const sortedKeys = [...functionKeys].sort((a, b) => {
            return (data.totals[b]?.count || 0) - (data.totals[a]?.count || 0);
        });

        return <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '15px', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #f0f0f0' }}>
            {sortedKeys.map((funcName) => {

                const originalIndex = functionKeys.indexOf(funcName);
                const entryColor = colors[originalIndex % colors.length];

                const stats = data.totals[funcName] || { count: 0, users: 0 };
                const isDimmed = highlight && highlight !== funcName;

                return <div
                    key={`legend-${funcName}`}
                    onMouseEnter={(e) => handleLegendEnter(e, funcName)}
                    onMouseMove={handleLegendMove}
                    onMouseLeave={handleLegendLeave}
                    style={{
                        display: 'flex', alignItems: 'center', fontSize: '13px', cursor: 'pointer',
                        color: isDimmed ? '#ccc' : '#444', transition: 'opacity 0.2s'
                    }}
                >
                    <div style={{ width: 10, height: 10, backgroundColor: entryColor, marginRight: 6, borderRadius: '50%' }} />
                    <span style={{ fontWeight: 600, marginRight: 4 }}>{funcName}</span>
                    <span style={{ color: isDimmed ? '#eee' : '#888', fontSize: '12px' }}>
                        ({formatCompactNumber(stats.count)}) <i>{stats.users} users</i>
                    </span>
                </div>
            })}
        </div>
    };
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
            return <div style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E0E0E0',
                padding: '12px',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
                <p style={{ fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #EEEEEE', paddingBottom: '4px' }}>
                    {formatDate(label)}
                </p>
                {sortedPayload.map((p, index) => {
                    const funcName = p.name;
                    const dailyStats = p.payload[funcName];
                    if (!dailyStats) { return null };
                    return <div
                        key={index}
                        style={{
                            color: p.color,
                            fontSize: '13px',
                            marginBottom: '4px',
                            fontWeight: highlight === funcName ? 600 : 300
                        }}>
                        <span>{funcName}:</span> {dailyStats.count} calls
                        <span style={{ color: '#888888', marginLeft: '6px', fontSize: '12px' }}>
                            (<i>{dailyStats.users} users</i>)
                        </span>
                    </div>;
                })}
            </div>;
        }
        return null;
    };

    return <div style={{ width: '100%', height: 500, padding: '20px', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
        {legendPopup.show && <LegendHistoryTooltip
            functionName={legendPopup.name}
            data={chartData}
            position={legendPopup}
        />}
        <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fill: "#666666", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    //scale="sqrt"
                    //domain={[0, 'dataMax']}
                    //tickFormatter={formatYAxis}
                    //width={45}
                    allowDecimals={false}
                    tick={{ fill: "#666666", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} isAnimationActive={false} wrapperStyle={{ zIndex: 10 }} />

                {Object.keys(data.totals).map((funcName, index) => {
                    const lineColor = colors[index % colors.length];
                    const isDimmed = highlight && highlight !== funcName;
                    return <Line
                        //isAnimationActive={false}
                        isAnimationActive={!isLoaded}
                        onMouseEnter={() => setHighlight(funcName)}
                        onMouseLeave={() => setHighlight(null)}
                        key={funcName}
                        name={funcName}
                        dataKey={`${funcName}.count`}
                        type="monotone"
                        stroke={lineColor}
                        strokeOpacity={isDimmed ? 0.5 : 1} //Line fades out others if one is active
                        strokeWidth={highlight === funcName ? 4 : 2} //Line thickens when active
                        dot={{
                            r: highlight === funcName ? 6 : 4,
                            strokeWidth: highlight === funcName ? 0 : 2,
                            fill: lineColor,
                            opacity: isDimmed ? 0.5 : 1,
                            stroke: '#FFFFFF'
                        }}
                        activeDot={{ r: isDimmed ? 4 : 6, opacity: isDimmed ? 0.5 : 1, strokeWidth: 0 }}
                        //z={highlight === funcName ? 100 : 1} //this didn't work for me
                        connectNulls={true}
                    />
                })}
            </LineChart>
        </ResponsiveContainer>
        {renderExternalLegend()}
    </div>;
};

const getFunctionUsage = async (apiToken, setAdminState) => {
    if (!apiToken) {
        console.log("no apiToken");
        return null;
    }
    console.log("CALLING SERVER with getduplicatetitles");
    await axiosInstance({
        method: 'POST',
        url: fevir_node_server_address,
        timeout: 120000,
        data: {
            'functionid': 'getfunctionusage',
            'apiToken': apiToken,
            'analyticsBypass': true
        },
    }).then((resp) => {
        if (resp?.data?.success) {
            console.log(resp.data.usageData);
            setAdminState(prevState => {
                return { ...prevState, "loading": false, "usageData": resp.data.usageData };
            });
            return resp.data;
        } else {
            //return { ...prevState, "loading": false };
        }
        console.log("NO RESPONSE from getduplicatetitles call");
    }).catch((error) => { console.log(error); });
}

// eslint-disable-next-line no-empty-pattern
const AdminPage3 = ({ }) => {
    const dispatch = useDispatch();

    const isAdmin = useSelector(selectCurrentUserAdmin);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const apiTokenRef = useRef(null);

    const [adminState, setAdminState] = useState({
        "loading": true,
        "usageData": null
    });

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR Admin"));
    }, []);

    useEffect(() => {
        //this apiTokenRef (arbitrary, it could be another ref for another)
        //is so it doesn't call the function twice on local because of strict mode,
        //this can be done in other places of our code
        //if we're annoyed by the double call on local
        if (isAdmin && apiToken && apiTokenRef.current !== apiToken) {
            apiTokenRef.current = apiToken;
            getFunctionUsage(apiToken, setAdminState);
        }
    }, [isAdmin, apiToken]);

    return <>
        {isAdmin &&
            <div>
                <Segment className={`containerSegment maxRemainderOfPageSegment`} raised>
                    <div>
                        {adminState.loading &&
                            <Dimmer className={"loadingDimmer"} active inverted>
                                <Loader inverted>Loading</Loader>
                            </Dimmer>
                        }
                        <span style={{ width: "100%", textAlign: "center" }}>
                            <h3>Admin Dashboard 3 - Function Usage (just loginserver.js for now)</h3>
                        </span>
                        <br />
                        {adminState.usageData && <AnalyticsChart data={adminState.usageData} />}
                        <br />
                        <br />
                    </div>
                </Segment>
            </div>
        }
    </>
}

export default AdminPage3;